import React, {useContext, useRef, useState} from "react";
import {
    DialogContent,
    DialogTitle,
    Grid, MenuItem, Select,
    TextField
} from "@mui/material";
import {Dialog2} from "../../misc/Dialog2";
import {SmoothActionBar} from "../../misc/SmoothActionBar";
import {api} from "../../api/API";
import {useNavigate} from "react-router-dom";
import {Project, UnitSystem} from "../../api/Projects";
import {ListContext} from "../../misc/lists/ListContext";
import {Company} from "../../api/Companies";
import {useUser} from "../../misc/Permission";
import {FindOrCreateCompany} from "./manage/summary/FindOrCreateCompany";
import {ProvinceSelector} from "../settings/companies/clients/ProvinceSelector";
import {Alternative, alternativeLabel} from "../../api/QuoteAlternatives";
import {AutoComplete} from "./shopdrawing/openings/AutoComplete";
import {useBackgroundAction} from "./manage/sov/UseBackgroundTask";

export function AddProjectModal(props: {
    edit?: Project;
    chooseAlternative?: Alternative[]
    defaultAlternative?: Alternative;
    defaultUnitSystem?: UnitSystem
    onClose(): void;
}) {

    const ctx = useContext(ListContext);
    const reload = useRef(ctx.reload);
    reload.current = ctx.reload;

    const [name, setName] = useState(props.edit?.name || "")
    const [street, setStreet] = useState(props.edit?.addressStreet || "")
    const [city, setCity] = useState(props.edit?.addressCity || "")
    const [prov, setProv] = useState(props.edit?.addressProvince || "")
    const [postal, setPostal] = useState(props.edit?.addressPostal || "")
    const [unitSystem, setUnitSystem] = useState(props.edit?.unitSystem || props.defaultUnitSystem || "imperial");
    const [alternative, setAlternative] = useState<Alternative|null>(props.defaultAlternative || null);

    const includeAlt = props.chooseAlternative !== undefined && props.chooseAlternative !== null;

    const navigate = useNavigate();
    const propsId = props.edit?.id;

    const action = useBackgroundAction({
        callback: (input: Parameters<typeof api.projects.upsertTask>[0]) =>  api.projects.upsertTask(input),
        onDone: (result) => {
            props.onClose()

            if(!propsId) {
                navigate("/project/" + result.id);
            } else {
                reload.current();
            }
        }
    });

    // separate hook so the archive/unarchive button has its own busy spinner (edit mode only)
    const archiveAction = useBackgroundAction({
        callback: (input: Parameters<typeof api.projects.upsertTask>[0]) => api.projects.upsertTask(input),
        onDone: () => {
            props.onClose()
            reload.current();
        }
    });

    const [client, setClient] = useState<Company|null>(null);
    const [contractor, setContractor] = useState<Company|null>(null);

    const me = useUser();

    const submit = () => {
        const changes: Partial<Project> & {copyFromAlternative?: number} = {
            name: name,
            client: client?.id,
            contractor: contractor?.id,
            quoteContractors: contractor ? [{
                contractor: contractor?.id,
            } as any] : [],
            unitSystem: unitSystem,
            addressStreet: street,
            addressCity: city,
            addressProvince: prov,
            addressPostal: postal,

            copyFromAlternative: alternative?.id,
        };

        if(!props.edit && me) {
            changes.estimators = [{person: me.id}] as any;
        }

        action.callback(Object.assign(props.edit || {}, changes))
    };

    return (
        <Dialog2 open onClose={props.onClose} onEnterPress={submit}>
            <DialogTitle>
                {props.edit ? "Edit Project" : "Create Project"}
            </DialogTitle>
            <DialogContent>
                <div style={{height: 8}} />
                <Grid container spacing={2} direction="column">
                    {includeAlt && <Grid item>
                        <AutoComplete<Alternative>
                            style={{minWidth: 350}}
                            label="Copy from Alternative"
                            search={async input => {
                                if(!props.chooseAlternative) return [];
                                return props.chooseAlternative.filter(a => alternativeLabel(a).toLowerCase().includes(input.toLowerCase()));
                            }}
                            fullWidth
                            value={alternative}
                            equals={(a, b) => a.id === b.id}
                            makeLabel={alternativeLabel}
                            onSelect={setAlternative}
                        />
                    </Grid>}
                    <Grid item>
                        <TextField label="Project Name" fullWidth
                                   value={name}
                                   onChange={e => setName(e.target.value)}
                                   style={{width: 300}}
                                   required
                        />
                    </Grid>
                    <Grid item>
                        <FindOrCreateCompany
                            label="General Contractor"
                            kind="contractor"
                            value={contractor}
                            onChange={setContractor}
                        />
                    </Grid>
                    <Grid item>
                        <Grid container direction="column" spacing={1}>
                            <Grid item>
                                    <TextField size="small" label="Project Street Address" fullWidth
                                               value={street}
                                               onChange={e => setStreet(e.target.value)}
                                               style={{width: 300}}
                                    />
                                </Grid><Grid item>
                                    <TextField size="small" label="City" fullWidth
                                               value={city}
                                               onChange={e => setCity(e.target.value)}
                                               style={{width: 300}}
                                    />
                            </Grid><Grid item>
                                <Grid container spacing={1}>
                                    <Grid item>
                                        <TextField size="small" label="Postal" fullWidth
                                                   value={postal}
                                                   onChange={e => setPostal(e.target.value)}
                                                   style={{width: 100}}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <ProvinceSelector size="small" value={prov} onChange={setProv} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <FindOrCreateCompany
                            label="Owner"
                            kind="client"
                            value={client}
                            onChange={setClient}
                        />
                    </Grid>
                    <Grid item>
                        <Select value={unitSystem} size="small"  onChange={(e, value) => setUnitSystem(e.target.value as any)}>
                            <MenuItem value="imperial">Imperial</MenuItem>
                            <MenuItem value="metric">Metric</MenuItem>
                        </Select>
                    </Grid>
                </Grid>
            </DialogContent>
            <SmoothActionBar
                loading={action.loading}
                error={action.error || archiveAction.error}
                actionName={props.edit ? "Update" : "Create"}
                onSubmit={submit}
                onCancel={props.onClose}
                onDestroy={props.edit ? () => {
                    if(!props.edit) return;

                    archiveAction.callback(Object.assign({}, props.edit, {
                        archived: !props.edit.archived,
                    }))
                } : undefined}
                destroyName={props.edit?.archived ? "Unarchive" : "Archive"}
                destroyLoading={archiveAction.loading}
            />
        </Dialog2>
    )
}